import { RELATIONSHIP_LENSES, type RelationshipLens } from '../data/graph-types.js';

export type AtlasViewMode = 'graph' | 'list';

export const DEFAULT_RELATIONSHIP_LENSES: readonly RelationshipLens[] = RELATIONSHIP_LENSES;

export type AtlasUrlState = {
  personId: string | null;
  traceFrom: string | null;
  traceTo: string | null;
  storyId: string | null;
  view: AtlasViewMode;
  lenses: readonly RelationshipLens[];
};

export type StoryEndpoints = {
  source: string;
  target: string;
};

export type AtlasStateTransitionContext = {
  tracePeople?: readonly string[] | null | undefined;
};

function validPerson(value: string | null, validIds: ReadonlySet<string>): string | null {
  return value !== null && validIds.has(value) ? value : null;
}

function parseLenses(value: string | null): readonly RelationshipLens[] {
  if (value === null) {
    return DEFAULT_RELATIONSHIP_LENSES;
  }
  const requested = new Set(value.split(','));
  const valid = RELATIONSHIP_LENSES.filter((lens) => requested.has(lens));
  return valid.length > 0 ? valid : DEFAULT_RELATIONSHIP_LENSES;
}

export function invalidAtlasUrlParameters(
  search: string,
  validIds: ReadonlySet<string>,
  validStories: ReadonlyMap<string, StoryEndpoints>,
): readonly string[] {
  const params = new URLSearchParams(search);
  const invalid = ['person', 'from', 'to'].filter((key) => {
    const value = params.get(key);
    return value !== null && !validIds.has(value);
  });
  const lensValue = params.get('lenses');
  if (
    lensValue !== null &&
    lensValue.split(',').some((lens) => !RELATIONSHIP_LENSES.includes(lens as RelationshipLens))
  ) {
    invalid.push('lenses');
  }
  const storyId = params.get('story');
  if (storyId !== null) {
    const story = validStories.get(storyId);
    if (
      story === undefined ||
      story.source !== params.get('from') ||
      story.target !== params.get('to')
    ) {
      invalid.push('story');
    }
  }
  return invalid;
}

export function parseAtlasUrlState(
  search: string,
  validIds: ReadonlySet<string>,
  validStories: ReadonlyMap<string, StoryEndpoints>,
): AtlasUrlState {
  const params = new URLSearchParams(search);
  const traceFrom = validPerson(params.get('from'), validIds);
  const traceTo = validPerson(params.get('to'), validIds);
  const requestedStory = params.get('story');
  const story = requestedStory === null ? undefined : validStories.get(requestedStory);
  return normalizeAtlasUrlState({
    personId: validPerson(params.get('person'), validIds),
    traceFrom,
    traceTo,
    storyId:
      requestedStory !== null && story?.source === traceFrom && story.target === traceTo
        ? requestedStory
        : null,
    view: params.get('view') === 'list' ? 'list' : 'graph',
    lenses: parseLenses(params.get('lenses')),
  });
}

export function normalizeAtlasUrlState(
  state: AtlasUrlState,
  context: AtlasStateTransitionContext = {},
): AtlasUrlState {
  if (state.traceFrom !== null && state.traceFrom === state.traceTo) {
    return {
      ...state,
      personId: state.traceFrom,
      traceFrom: null,
      traceTo: null,
      storyId: null,
    };
  }

  const traceEndpoints = [state.traceFrom, state.traceTo].filter(
    (personId): personId is string => personId !== null,
  );
  const personBelongsToTrace =
    state.personId === null ||
    traceEndpoints.includes(state.personId) ||
    context.tracePeople?.includes(state.personId) === true;
  const canEvaluateTraceMembership = traceEndpoints.length < 2 || context.tracePeople !== undefined;
  const shouldClearUnrelatedTrace =
    traceEndpoints.length > 0 && canEvaluateTraceMembership && !personBelongsToTrace;
  const traceFrom = shouldClearUnrelatedTrace ? null : state.traceFrom;
  const traceTo = shouldClearUnrelatedTrace ? null : state.traceTo;
  const storyId =
    shouldClearUnrelatedTrace || traceFrom === null || traceTo === null ? null : state.storyId;

  if (traceFrom === state.traceFrom && traceTo === state.traceTo && storyId === state.storyId) {
    return state;
  }
  return { ...state, traceFrom, traceTo, storyId };
}

export function transitionAtlasUrlState(
  current: AtlasUrlState,
  changes: Partial<AtlasUrlState>,
  context: AtlasStateTransitionContext = {},
): AtlasUrlState {
  const changesTraceIdentity =
    'traceFrom' in changes || 'traceTo' in changes || 'lenses' in changes;
  return normalizeAtlasUrlState(
    {
      ...current,
      ...changes,
      ...(changesTraceIdentity && !('storyId' in changes) ? { storyId: null } : {}),
    },
    context,
  );
}

export function serializeAtlasUrlState(state: AtlasUrlState): string {
  const normalized = normalizeAtlasUrlState(state);
  const params = new URLSearchParams();
  if (normalized.personId !== null) {
    params.set('person', normalized.personId);
  }
  if (normalized.traceFrom !== null) {
    params.set('from', normalized.traceFrom);
  }
  if (normalized.traceTo !== null) {
    params.set('to', normalized.traceTo);
  }
  if (normalized.storyId !== null && normalized.traceFrom !== null && normalized.traceTo !== null) {
    params.set('story', normalized.storyId);
  }
  const lenses = RELATIONSHIP_LENSES.filter((lens) => normalized.lenses.includes(lens));
  if (
    lenses.length > 0 &&
    (lenses.length !== DEFAULT_RELATIONSHIP_LENSES.length ||
      lenses.some((lens, index) => lens !== DEFAULT_RELATIONSHIP_LENSES[index]))
  ) {
    params.set('lenses', lenses.join(','));
  }
  if (normalized.view === 'list') {
    params.set('view', 'list');
  }
  const encoded = params.toString();
  return encoded.length === 0 ? '' : `?${encoded}`;
}
